/**
 * ============================================================================
 * MEGA MENU COLLECTION GRID COMPONENT
 * ============================================================================
 *
 * Enhanced mega menu with collection grid layout featuring square images,
 * hover effects, and responsive behavior.
 *
 * ============================================================================
 */

class MegaMenuCollectionGrid {
  constructor(element) {
    this.element = element;
    this.cards = this.element.querySelectorAll('.mega-menu-collection-card');
    this.isVisible = false;
    this.animationDelay = 100;

    this.init();
  }

  /**
   * Initialize the mega menu collection grid
   */
  init() {
    this.setupIntersectionObserver();
    this.bindEvents();
    this.preloadImages();
    console.log('🎯 Mega Menu Collection Grid initialized');
  }

  /**
   * Setup intersection observer for performance optimization
   */
  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.isVisible) {
          this.isVisible = true;
          this.animateIn();
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '50px'
    });

    observer.observe(this.element);
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    this.cards.forEach((card, index) => {
      // Hover effects
      card.addEventListener('mouseenter', () => this.handleCardHover(card, true));
      card.addEventListener('mouseleave', () => this.handleCardHover(card, false));

      // Click tracking for analytics
      card.addEventListener('click', (e) => this.handleCardClick(e, card));

      // Touch support for mobile
      card.addEventListener('touchstart', () => this.handleCardTouch(card), { passive: true });
    });

    // Keyboard navigation
    this.element.addEventListener('keydown', (e) => this.handleKeyboardNavigation(e));

    // Prevent background scroll when mega menu is scrolling
    this.element.addEventListener('wheel', (e) => this.handleScroll(e), { passive: false });
    this.element.addEventListener('touchmove', (e) => this.handleTouchScroll(e), { passive: false });
  }

  /**
   * Handle card hover effects
   */
  handleCardHover(card, isHovering) {
    const image = card.querySelector('.mega-menu-collection-card__img');
    const overlay = card.querySelector('.mega-menu-collection-card__overlay');
    const title = card.querySelector('.mega-menu-collection-card__title');

    if (isHovering) {
      card.style.transform = 'translateY(-4px)';
      if (image) image.style.transform = 'scale(1.05)';
      if (overlay) overlay.style.opacity = '0.7';
      if (title) title.style.transform = 'translateY(-2px)';
    } else {
      card.style.transform = '';
      if (image) image.style.transform = '';
      if (overlay) overlay.style.opacity = '';
      if (title) title.style.transform = '';
    }
  }

  /**
   * Handle card click for analytics
   */
  handleCardClick(event, card) {
    const collectionHandle = card.dataset.collectionHandle;
    const collectionTitle = card.querySelector('.mega-menu-collection-card__title')?.textContent;

    // Analytics tracking
    if (window.gtag) {
      window.gtag('event', 'mega_menu_collection_click', {
        collection_handle: collectionHandle,
        collection_title: collectionTitle,
        menu_type: 'collection_grid'
      });
    }

    // Custom event for other tracking systems
    this.element.dispatchEvent(new CustomEvent('megaMenuCollectionClick', {
      detail: {
        collectionHandle,
        collectionTitle,
        card
      }
    }));
  }

  /**
   * Handle touch interactions for mobile
   */
  handleCardTouch(card) {
    // Add touch feedback
    card.style.transform = 'scale(0.98)';
    setTimeout(() => {
      card.style.transform = '';
    }, 150);
  }

  /**
   * Handle keyboard navigation
   */
  handleKeyboardNavigation(event) {
    const focusedCard = document.activeElement;
    if (!focusedCard.classList.contains('mega-menu-collection-card')) return;

    const currentIndex = Array.from(this.cards).indexOf(focusedCard);
    let nextIndex;

    // Get current number of columns based on screen width
    const screenWidth = window.innerWidth;
    let columns;
    if (screenWidth >= 1200) {
      columns = 5;
    } else if (screenWidth >= 990) {
      columns = 4;
    } else if (screenWidth >= 750) {
      columns = 3;
    } else if (screenWidth >= 550) {
      columns = 2;
    } else {
      columns = 1;
    }

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.cards.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.cards.length) % this.cards.length;
        break;
      case 'ArrowDown':
        // Move to next row based on current column count
        nextIndex = Math.min(currentIndex + columns, this.cards.length - 1);
        break;
      case 'ArrowUp':
        // Move to previous row based on current column count
        nextIndex = Math.max(currentIndex - columns, 0);
        break;
      default:
        return;
    }

    event.preventDefault();
    this.cards[nextIndex]?.focus();
  }

  /**
   * Animate cards in when visible (optimized for many cards)
   */
  animateIn() {
    // For performance with many cards, animate in batches
    const batchSize = 8;
    const batches = [];

    for (let i = 0; i < this.cards.length; i += batchSize) {
      batches.push(Array.from(this.cards).slice(i, i + batchSize));
    }

    batches.forEach((batch, batchIndex) => {
      batch.forEach((card, cardIndex) => {
        card.style.opacity = '0';
        card.style.transform = 'translateY(20px)';

        setTimeout(() => {
          card.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
          card.style.opacity = '1';
          card.style.transform = 'translateY(0)';
        }, (batchIndex * 50) + (cardIndex * 20));
      });
    });
  }

  /**
   * Preload images for better performance (optimized for many images)
   */
  preloadImages() {
    const images = this.element.querySelectorAll('.mega-menu-collection-card__img');

    // For many images, only preload the first visible ones immediately
    const visibleCount = Math.min(16, images.length); // First 2 rows on large screens

    images.forEach((img, index) => {
      if (index < visibleCount) {
        // Preload first visible images immediately
        if (img.loading === 'lazy') {
          img.loading = 'eager';
        }
      } else {
        // Keep lazy loading for others but optimize intersection observer
        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const image = entry.target;
                if (image.loading === 'lazy') {
                  image.loading = 'eager';
                }
                observer.unobserve(image);
              }
            });
          }, {
            rootMargin: '50px'
          });
          observer.observe(img);
        }
      }
    });
  }

  /**
   * Handle scroll events to prevent background scrolling
   */
  handleScroll(event) {
    const { scrollTop, scrollHeight, clientHeight } = this.element;
    const isScrollingUp = event.deltaY < 0;
    const isScrollingDown = event.deltaY > 0;

    // Always prevent background scroll - body is already locked
    event.preventDefault();
    event.stopPropagation();

    // Handle mega menu internal scrolling
    if (scrollHeight > clientHeight) {
      if (isScrollingDown && scrollTop < scrollHeight - clientHeight) {
        this.element.scrollTop += event.deltaY * 0.5;
      } else if (isScrollingUp && scrollTop > 0) {
        this.element.scrollTop += event.deltaY * 0.5;
      }
    }
  }

  /**
   * Handle touch scroll events
   */
  handleTouchScroll(event) {
    const { scrollTop, scrollHeight, clientHeight } = this.element;

    // Always prevent background scroll - body is already locked
    event.preventDefault();
    event.stopPropagation();

    // Let the mega menu handle its own touch scrolling naturally
    // The preventDefault above stops background scroll, but allows internal scroll
  }

  /**
   * Update grid layout based on screen size
   */
  updateLayout() {
    const screenWidth = window.innerWidth;
    let columns;

    if (screenWidth >= 1200) {
      columns = 5;
    } else if (screenWidth >= 990) {
      columns = 4;
    } else if (screenWidth >= 750) {
      columns = 3;
    } else if (screenWidth >= 550) {
      columns = 2;
    } else {
      columns = 1;
    }

    this.element.style.setProperty('--grid-columns', columns);
  }

  /**
   * Destroy the component
   */
  destroy() {
    this.cards.forEach((card) => {
      card.removeEventListener('mouseenter', this.handleCardHover);
      card.removeEventListener('mouseleave', this.handleCardHover);
      card.removeEventListener('click', this.handleCardClick);
      card.removeEventListener('touchstart', this.handleCardTouch);
    });

    this.element.removeEventListener('keydown', this.handleKeyboardNavigation);
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const megaMenuGrids = document.querySelectorAll('.mega-menu-collection-grid');
  megaMenuGrids.forEach((grid) => {
    new MegaMenuCollectionGrid(grid);
  });
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = MegaMenuCollectionGrid;
}

// Globale Verfügbarkeit
window.MegaMenuCollectionGrid = MegaMenuCollectionGrid;
